import { initScene, dispatchSceneLoadedEvent, updateSceneContent } from './scene-builder';
import * as THREE from 'three';
import type { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import type { Object3D, PerspectiveCamera, Scene, WebGLRenderer } from 'three';

let vrScene: Scene | null = null;
let vrCamera: PerspectiveCamera | null = null;
let vrRenderer: WebGLRenderer | null = null;
let vrOrbitControls: OrbitControls | null = null;

const arStartBtn = document.getElementById('ar-start-btn');
arStartBtn?.addEventListener('click', async () => {
  stopThreeJS();
});

window.addEventListener('resize', onVRResize);

export async function showVROrbitView() {
  await startThreeJSForVROrbitView();
}

async function startThreeJSForVROrbitView() {
  const canvas = document.getElementById('canvas') as HTMLCanvasElement | null;
  if (!canvas) {
    throw new Error('Canvas element #canvas not found');
  }
  const camerafeed = document.getElementById('camerafeed');
  if (camerafeed) camerafeed.style.display = 'none';

  canvas.style.display = 'block';

  vrScene = new THREE.Scene();
  vrScene.background = new THREE.Color(0xd6dcdc);
  vrCamera = new THREE.PerspectiveCamera(70, window.innerWidth / window.innerHeight, 0.01, 2000);

  vrRenderer = new THREE.WebGLRenderer({ canvas, antialias: true });
  vrRenderer.setPixelRatio(window.devicePixelRatio);
  vrRenderer.setSize(window.innerWidth, window.innerHeight);
  vrRenderer.toneMapping = THREE.ACESFilmicToneMapping;
  vrRenderer.toneMappingExposure = 1;
  vrRenderer.setAnimationLoop(vrAnimate);

  const { OrbitControls } = await import('three/addons/controls/OrbitControls.js');
  vrOrbitControls = new OrbitControls(vrCamera, canvas);
  vrOrbitControls.enableDamping = true;
  vrOrbitControls.dampingFactor = 0.05;

  await initScene(
    {
      scene: vrScene,
      camera: vrCamera,
      renderer: vrRenderer,
      clickReceiver: canvas,
      defaultObjectPosition: new THREE.Vector3(0, 0, 0),
      isVrOrbitView: true,
    },
    (modelRoot) => onModelLoaded(vrCamera!, vrOrbitControls!, modelRoot)
  );
  dispatchSceneLoadedEvent();
}

function onModelLoaded(
  camera: PerspectiveCamera,
  controls: OrbitControls,
  objectRoot: Object3D,
  margin = 1.2
) {
  objectRoot.updateMatrixWorld(true);
  const box = new THREE.Box3().setFromObject(objectRoot);
  const center = new THREE.Vector3();
  const sphere = new THREE.Sphere();
  box.getBoundingSphere(sphere);
  box.getCenter(center);

  controls.target.copy(center);

  const vfov = (camera.fov * Math.PI) / 180;
  const aspect = camera.aspect;
  const r = sphere.radius * margin;
  const distV = r / Math.tan(vfov / 2);
  const distH = r / (Math.tan(vfov / 2) * aspect);
  const distance = Math.max(distV, distH);

  const offsetDir = new THREE.Vector3(0, 0.22, 1).normalize();
  camera.position.copy(center).addScaledVector(offsetDir, distance);
  controls.update();
}

export function stopThreeJS() {
  vrRenderer?.setAnimationLoop(null);
  vrRenderer?.dispose();
  vrScene = null;
  vrCamera = null;
  vrRenderer = null;
  vrOrbitControls = null;
}

export function onVRResize() {
  if (!vrCamera || !vrRenderer) return;
  vrCamera.aspect = window.innerWidth / window.innerHeight;
  vrCamera.updateProjectionMatrix();
  vrRenderer.setSize(window.innerWidth, window.innerHeight);
}

export function vrAnimate() {
  if (vrOrbitControls) vrOrbitControls.update();
  updateSceneContent();
  if (vrRenderer && vrScene && vrCamera) vrRenderer.render(vrScene, vrCamera);
}
